import Cookies from 'js-cookie'
import _ from 'lodash'
import * as graph from '@adc/sdk'
import eventNames from '../common/eventNames'
import standardTracking from '../common/standardTracking'

// Type definitions
interface EventConfig {
  enabled?: boolean
  name?: string
  extractData?: (data: any) => any
}

interface AmplitudeEventConfig {
  [key: string]: EventConfig
}
interface AmplitudeEcommerceItem {
  morpheusListingId: string | undefined
  propertyAddress: string | undefined
  morpheusPropertyId: string | undefined
  recommendationListName: string | undefined
  recommendationModelName: string | undefined
  propertyIndex: number | undefined
  carouselPosition: number | undefined
  listingType: string | undefined
  propertyType: string | undefined
  boosted: string | undefined
  matchingScore: string | undefined
  estimatedMarketValue: number | undefined
}
interface AnalyticsData {
  userId?: string | null
  morpheusListingId?: string | null
  morpheusPropertyId?: string | null
  pageClassification?: string | null
  cookieId?: string
  listing?: any
  user?: {
    logged_in?: boolean
    user_id?: string
    party_id?: string
  }
  featureFlags?: Record<string, any>
  listing_configuration?: {
    asset_type?: string
  }
  virtualUrl?: string
  virtualTitle?: string
  numberSearchResults?: number
  newSearch?: boolean
  status?: string
  statusLabel?: string
  [key: string]: any
}

interface PageContext {
  pageClassification: string | null
  userId?: string | null
  partyId?: string | null
  morpheusPropertyId?: string | null
  morpheusListingId?: string | null
  statusLabel?: string | null
  isVirtual?: boolean
  aiAssistant?: 'enabled' | 'disabled'
  [key: string]: any
}

const toLowerCamelCaseKey = (key: string): string => {
  return key.replace(/_+([a-z0-9])/gi, (_, c) => c.toUpperCase())
}

const keysToLowerCamelCase = (input: any): any => {
  if (Array.isArray(input)) {
    return input.map(keysToLowerCamelCase)
  }

  if (input !== null && typeof input === 'object') {
    return Object.entries(input).reduce((acc: any, [key, value]) => {
      acc[toLowerCamelCaseKey(key)] = keysToLowerCamelCase(value)
      return acc
    }, {})
  }

  // primitives (string, number, boolean, null, undefined)
  return input
}

const extractStandardTrackingData = (data: AnalyticsData): any => {
  const {
    userId = null,
    morpheusListingId = null,
    morpheusPropertyId = null,
    pageClassification = null,
    cookieId = Cookies.get('BRID'),
    ...rest
  } = data

  const payload: any = {}
  if (morpheusListingId) payload.morpheusListingId = morpheusListingId
  if (morpheusPropertyId) payload.morpheusPropertyId = morpheusPropertyId

  if (userId) payload.userId = userId

  if (pageClassification) payload.pageClassification = pageClassification

  return { adcCookieId: cookieId, ...payload, ...keysToLowerCamelCase(rest) }
}

// per amplitude folks:
// In order to report properly on the ‘Cart’ functionality,
// we’ll need to track the listing_id and morpheus_property_id in an array.
const getItems = (data: AnalyticsData) => {
  const item: Partial<
    Record<
      'morpheusPropertyId' | 'morpheusListingId' | 'propertyAddress',
      string | number
    >
  > = {}
  if (data.morpheusListingId) {
    item.morpheusListingId = data.morpheusListingId
  }

  if (data.morpheusPropertyId) {
    item.morpheusPropertyId = data.morpheusPropertyId
  }

  if (data.propertyAddress) {
    item.propertyAddress = data.propertyAddress
  }

  return { items: [item] }
}

const extractStandardTrackingDataWithItems = (data: AnalyticsData) => {
  return {
    ...extractStandardTrackingData(data),
    ...getItems(data),
  }
}

const mapEcommerceItems = (items: any[]): AmplitudeEcommerceItem[] =>
  items?.map(item => ({
    morpheusListingId: item.item_id,
    propertyAddress: item.item_name,
    morpheusPropertyId: item.morpheus_property_id,
    recommendationListName: item.item_list_id,
    recommendationModelName: item.item_list_name,
    propertyIndex: item.index,
    carouselPosition: item.carousel_position,
    listingType: item.item_category,
    propertyType: item.item_category2,
    boosted: item.boosted,
    matchingScore: item.matchingScore,
    estimatedMarketValue: item.price,
  }))

const recommendationClickEvents: AmplitudeEventConfig = {
  // Property Recommendation Click
  // All 4 gtm events down below
  // were grouped as recommendation_click in amplitude POC gmt implementation
  [eventNames.carouselPropertyClicked]: {
    name: 'recommendation_click',
    // TODO: data-elm-id should not contain save_property_icon per gtm implementation
    extractData: extractStandardTrackingData,
  },
  [eventNames.photoGridPropertyClicked]: {
    name: 'recommendation_click',
    // TODO: data-elm-id should not contain save_property_icon per gtm implementation
    extractData: extractStandardTrackingData,
  },
  [eventNames.propertyRecommendationsPropertyClick]: {
    name: 'recommendation_click',
    extractData: extractStandardTrackingData,
  },
  [eventNames.similarPropertiesClick]: {
    name: 'recommendation_click',
    extractData: extractStandardTrackingData,
  },
}

const registrationEvents = {
  [eventNames.bidRegistrationStart]: {
    name: 'bid_registration_start',
  },
  [eventNames.reoBidderRegistrationFormSubmit]: {
    name: 'registration_submit',
    extractData: extractStandardTrackingDataWithItems,
  },
  [eventNames.registrationContactDetailsCompleted]: {
    name: 'registration_contact_details_completed',
  },
  [eventNames.registrationAuctionTermsCompleted]: {
    name: 'registration_auction_terms_completed',
  },
  [eventNames.registrationDepositRequiredCompleted]: {
    name: 'registration_deposit_required_completed',
  },
  [eventNames.registrationFlowCompleted]: {
    name: 'registration_flow_completed',
  },
}

// Apps enrich these with standardTracking.user before firing;
// extractStandardTrackingData adds adcCookieId and camelCases the rest. The
// Amplitude name defaults to the event key (eventNames value) — no need to
// repeat the string here.
const buyingPreferencesEvents: AmplitudeEventConfig = {
  [eventNames.preferencesCenterVisited]: {
    extractData: extractStandardTrackingData,
  },
  [eventNames.preferencesCenterEdited]: {
    extractData: extractStandardTrackingData,
  },
  [eventNames.preferencesCenterUpdated]: {
    extractData: extractStandardTrackingData,
  },
  [eventNames.preferencesCenterConfirmed]: {
    extractData: extractStandardTrackingData,
  },
  [eventNames.preferencesCenterSkipped]: {
    extractData: extractStandardTrackingData,
  },
  [eventNames.nudgeShown]: {
    extractData: extractStandardTrackingData,
  },
  [eventNames.nudgeCompleted]: {
    extractData: extractStandardTrackingData,
  },
  [eventNames.nudgeSkipped]: {
    extractData: extractStandardTrackingData,
  },
  [eventNames.nudgeDismissed]: {
    extractData: extractStandardTrackingData,
  },
  [eventNames.buyingPreferencesCarouselImpression]: {
    extractData: extractStandardTrackingData,
  },
  [eventNames.buyingPreferencesCarouselUpdatePrefsClick]: {
    extractData: extractStandardTrackingData,
  },
  [eventNames.buyingPreferencesCarouselTabClick]: {
    extractData: extractStandardTrackingData,
  },
  [eventNames.buyingPreferencesCarouselLoginCtaClick]: {
    extractData: extractStandardTrackingData,
  },

  // Logged-out homepage value prop treatment (MERCH-42). pageClassification
  // is auto-attached by the pageview-enrichment plugin and loggedIn is a user
  // property, so no extra event properties are needed.
  [eventNames.buyingPreferencesValuePropShown]: {
    extractData: extractStandardTrackingData,
  },
  [eventNames.buyingPreferencesSignupCtaClicked]: {
    extractData: extractStandardTrackingData,
  },
  [eventNames.buyingPreferencesLoginCtaClicked]: {
    extractData: extractStandardTrackingData,
  },
}

const propertySaveEvents: AmplitudeEventConfig = {
  // Property Save
  // All 4 gtm events down below
  // were grouped as property_save in amplitude POC gmt implementation
  [eventNames.propertySave]: {
    name: 'property_save',
    // TODO: Page Path does not equal /
    // TODO: DL-action equals save
    extractData: extractStandardTrackingDataWithItems,
  },
  [eventNames.propertyRecommendationsPropertySave]: {
    name: 'property_save',
    extractData: extractStandardTrackingDataWithItems,
  },
  // TODO: DL-action equals save
  [eventNames.similarPropertySave]: {
    name: 'property_save',
    extractData: extractStandardTrackingDataWithItems,
  },
  [eventNames.srpPropertySave]: {
    name: 'property_save',
    extractData: extractStandardTrackingDataWithItems,
  },
  [eventNames.srpPropertyClick]: {
    name: 'srp_property_click',
    extractData: extractStandardTrackingDataWithItems,
  },
  [eventNames.srpPropertyClickMobile]: {
    name: 'srp_property_click_mobile',
    extractData: extractStandardTrackingDataWithItems,
  },
  [eventNames.prpPropertySave]: {
    name: 'prp_property_save',
    extractData: data => {
      const { item_list_id, ...rest } = data
      return {
        ...extractStandardTrackingData(rest),
        recommendationType: item_list_id,
      }
    },
  },
}

const amplitudeEventConfig: AmplitudeEventConfig = {
  ...recommendationClickEvents,
  ...propertySaveEvents,
  ...registrationEvents,
  ...buyingPreferencesEvents,
  [eventNames.dualTrackPathSelected]: {
    name: 'dual_track_path_selected',
    extractData: data => {
      const { path, source, ...rest } = data
      return {
        path,
        source,
        ...extractStandardTrackingData(rest),
      }
    },
  },
  [eventNames.dualTrackModalViewed]: {
    name: 'dual_track_modal_viewed',
    extractData: extractStandardTrackingData,
  },
  [eventNames.dualTrackModalOutsideClick]: {
    name: 'dual_track_modal_outside_click',
    extractData: extractStandardTrackingData,
  },

  // Dashboard redesign / Your Feed (MERCH-172). Emitted by resi-fe-dashboard
  // (feedRecentUpdates hide action and feed utility cards). Standard
  // extraction passes the event facets through (pageId, card, action,
  // morpheusPropertyId, propertyBadges).
  [eventNames.propertyHide]: {
    extractData: extractStandardTrackingData,
  },
  [eventNames.utilityCardClick]: {
    extractData: extractStandardTrackingData,
  },
  [eventNames.yourFeedBatchLoad]: {
    extractData: extractStandardTrackingData,
  },
  [eventNames.yourFeedEndReached]: {
    extractData: extractStandardTrackingData,
  },
  [eventNames.homepageCarouselTitleClick]: {
    name: 'homepage_carousel_title_click',
    extractData: data => {
      const { item_list_id, item_list_name, ...rest } = data
      return {
        ...extractStandardTrackingData(rest),
        recommendationListName: item_list_id,
        recommendationModelName: item_list_name,
      }
    },
  },
  [eventNames.homepageCarouselPropertySave]: {
    name: 'homepage_carousel_property_save',
    extractData: data => {
      const { item_list_id, ...rest } = data
      return {
        ...extractStandardTrackingDataWithItems(rest),
        recommendationType: item_list_id,
      }
    },
  },
  [eventNames.selectItem]: {
    name: 'item_selected',
    extractData: data => {
      const { ecommerce, carousel_position, ...rest } = data
      return {
        ...extractStandardTrackingData(rest),
        recommendationListName: ecommerce?.item_list_id,
        recommendationModelName: ecommerce?.item_list_name,
        carouselPosition: carousel_position,
        items: mapEcommerceItems(ecommerce?.items),
      }
    },
  },
  [eventNames.viewItemList]: {
    name: 'item_list_viewed',
    extractData: data => {
      const { ecommerce, carousel_position, ...rest } = data
      return {
        ...extractStandardTrackingData(rest),
        recommendationListName: ecommerce?.item_list_id,
        recommendationModelName: ecommerce?.item_list_name,
        carouselPosition: carousel_position,
        items: mapEcommerceItems(ecommerce?.items),
      }
    },
  },
  [eventNames.confirmBid]: {
    name: 'confirm_bid',
    extractData: extractStandardTrackingDataWithItems,
  },
  [eventNames.download]: {
    name: 'document_download',
    extractData: extractStandardTrackingData,
  },
  [eventNames.makeAnOfferSubmit]: {
    name: 'offer_submit',
    extractData: extractStandardTrackingData,
  },
  [eventNames.virtualPageview]: {
    name: 'page_view',
    extractData: (data: any) => {
      const payload: any = { isVirtual: true }
      const {
        morpheusListingId,
        morpheusPropertyId,
        virtualUrl,
        virtualTitle,
        numberSearchResults,
        newSearch,
        status,
        statusLabel,
        ...rest
      } = data

      if (morpheusListingId) payload.morpheusListingId = morpheusListingId
      if (morpheusPropertyId) payload.morpheusPropertyId = morpheusPropertyId
      if (virtualUrl) {
        payload.pageUrl = virtualUrl
        payload.pagePath = virtualUrl
      }
      if (virtualTitle) payload.pageTitle = virtualTitle
      if (numberSearchResults) payload.numberSearchResults = numberSearchResults
      if (newSearch) payload.newSearch = newSearch
      if (status) payload.status = status
      if (statusLabel) payload.statusLabel = statusLabel

      return {
        ...payload,
        ...keysToLowerCamelCase(rest),
      }
    },
  },
  [eventNames.pageLoaded]: {},
  [eventNames.photoGalleryMounted]: {},
  [eventNames.photoGalleryOpen]: {},
  [eventNames.photoGalleryViewTab]: {},
  [eventNames.pdpAiAssistantPromptSubmit]: {
    name: 'chat_engaged',
    extractData: extractStandardTrackingData,
  },
  [eventNames.pdpAiConversationStart]: {
    name: 'conversation_started',
    extractData: extractStandardTrackingData,
  },
  [eventNames.liveAgentBannerDisplayed]: {
    name: 'live_agent_banner_displayed',
    extractData: extractStandardTrackingData,
  },
  [eventNames.liveAgentBannerClicked]: {
    name: 'live_agent_banner_clicked',
    extractData: extractStandardTrackingData,
  },

  // max bid
  [eventNames.pdpSetAutoBidConfirm]: {
    name: 'max_bid_set',
    extractData: extractStandardTrackingData,
  },

  [eventNames.pdpIncreaseAutoBidConfirm]: {
    name: 'max_bid_increase',
    extractData: extractStandardTrackingData,
  },
  [eventNames.dashboardSetAutoBidConfirm]: {
    name: 'max_bid_set',
    extractData: extractStandardTrackingData,
  },
  [eventNames.dashboardIncreaseAutoBidConfirm]: {
    name: 'max_bid_increase',
    extractData: extractStandardTrackingData,
  },
  // remote bid qualification
  [eventNames.remoteBidQualified]: {
    name: 'remote_bid_qualified',
    extractData: extractStandardTrackingData,
  },

  [eventNames.navigationClick]: {
    name: 'navigation_click',
    extractData: extractStandardTrackingData,
  },
  [eventNames.buyerDashboardNavClick]: {
    name: 'buyer_dashboard_nav_click',
    extractData: extractStandardTrackingData,
  },
  [eventNames.dashboardBannerDisplayed]: {
    extractData: extractStandardTrackingData,
  },
  [eventNames.dashboardModalOpened]: {
    extractData: extractStandardTrackingData,
  },
  [eventNames.pdpEmdBadgeExpanded]: {
    extractData: extractStandardTrackingData,
  },
  [eventNames.accountNavClick]: {
    name: 'account_nav_click',
    extractData: extractStandardTrackingData,
  },
  [eventNames.srpFiltersButtonClick]: {
    name: 'srp_filters_button_click',
    extractData: extractStandardTrackingData,
  },
  [eventNames.searchFilterChanged]: {
    name: 'search_filter_changed',
    extractData: extractStandardTrackingData,
  },
  [eventNames.sort]: {
    name: 'search_sorted',
    extractData: extractStandardTrackingData,
  },
  [eventNames.searchAreaFilter]: {
    name: 'search_area_filtered',
    extractData: extractStandardTrackingData,
  },

  'account-create': {
    name: 'account_creation',
    extractData: (data: any) => {
      const { userId = null, cookieId = Cookies.get('BRID'), ...rest } = data

      return {
        userId,
        adcCookieId: cookieId,
        // TODO: need to pass additional data in gtmAuthSuccess in adc/ui-adc
        pageClassification: null,
        ...keysToLowerCamelCase(rest),
      }
    },
  },
  // Flow tracking events
  [eventNames.flowStepViewed]: {
    name: 'flow_step_viewed',
    extractData: extractStandardTrackingData,
  },
  [eventNames.flowAdvanced]: {
    name: 'flow_advanced',
    extractData: extractStandardTrackingData,
  },
  [eventNames.flowRetreated]: {
    name: 'flow_retreated',
    extractData: extractStandardTrackingData,
  },
  [eventNames.flowCompleted]: {
    name: 'flow_completed',
    extractData: extractStandardTrackingData,
  },
  [eventNames.flowSubmitted]: {
    name: 'flow_submitted',
    extractData: extractStandardTrackingData,
  },
  [eventNames.flowGoto]: {
    name: 'flow_goto',
    extractData: extractStandardTrackingData,
  },
  [eventNames.flowReturn]: {
    name: 'flow_return',
    extractData: extractStandardTrackingData,
  },

  'account-login': {
    name: 'login',
    extractData: (data: any) => {
      const { userId = null, cookieId = Cookies.get('BRID'), ...rest } = data

      return {
        userId,
        adcCookieId: cookieId,
        // TODO: need to pass additional data in gtmAuthSuccess in adc/ui-adc
        pageClassification: null,
        ...keysToLowerCamelCase(rest),
      }
    },
  },
}

/**
 * Get Amplitude event configuration with default values
 * @param event - The event name (e.g., 'photo_gallery_mounted')
 * @returns Event config with defaults applied, or null if event not in config
 */
const getAmplitudeEventConfig = (event: string): EventConfig | null => {
  const config = amplitudeEventConfig[event]

  if (!config) {
    return null
  }

  return {
    enabled: config.enabled !== undefined ? config.enabled : true,
    name: config.name || event,
    extractData: config.extractData || _.identity,
  }
}

enum PageClassification {
  PromotionsPage = 'Promotions Page',
  Contracting = 'Contracting',
  AboutUs = 'About Us',
  ContactUs = 'Contact Us',
  Legal = 'Legal',
  PropertyTracker = 'Property Tracker',
  CareersPages = 'Careers Pages',
  AgentPages = 'Agent Pages',
  UnsubscribePages = 'Unsubscribe Pages',
  HomepageView = 'Homepage View',
  SRPView = 'SRP View',
  REOPDPView = 'REO PDP View',
  TPSPDPView = 'TPS PDP View',
  PrivateSellerPDPView = 'Private Seller PDP View',
  MediaLPView = 'Media LP View',
  ALCStatePage = 'ALC State Page',
  CalendarAndEventsView = 'Calendar & Events View',
  Registration = 'Registration',
  HelpCenter = 'Help Center',
  Dashboard = 'Dashboard',
  Blog = 'Blog',
}

const pageClassificationConstants = {
  PromotionsPage: 'Promotions Page',
  Contracting: 'Contracting',
  AboutUs: 'About Us',
  ContactUs: 'Contact Us',
  Legal: 'Legal',
  PropertyTracker: 'Property Tracker',
  CareersPages: 'Careers Pages',
  AgentPages: 'Agent Pages',
  UnsubscribePages: 'Unsubscribe Pages',
  HomepageView: 'Homepage View',
  SRPView: 'SRP View',
  REOPDPView: 'REO PDP View',
  TPSPDPView: 'TPS PDP View',
  PrivateSellerPDPView: 'Private Seller PDP View',
  MediaLPView: 'Media LP View',
  ALCStatePage: 'ALC State Page',
  CalendarAndEventsView: 'Calendar & Events View',
  Registration: 'Registration',
  HelpCenter: 'Help Center',
  Dashboard: 'Dashboard',
  Blog: 'Blog',
}

// TODO: at the moment this mirrors the functionality of Sheldon's custom js in
// GTM variable "CJS - GA4 - Page Classification - Content Grouping - new"
// We can get more granular with the classifications later if needed.
const getPageClassification = (data: AnalyticsData = {}): string | null => {
  const pathname = window.location.pathname.toLowerCase()
  if (pathname.includes('/contract/')) {
    return PageClassification.Contracting
  }

  if (pathname.includes('unsubscribe')) {
    return PageClassification.UnsubscribePages
  }

  if (pathname === '/') {
    return PageClassification.HomepageView
  }

  if (pathname.match(/^\/residential/)) {
    return PageClassification.SRPView
  }

  if (pathname.match(/^\/details/)) {
    const assetType = data?.listing_configuration?.asset_type || ''

    // TODO: use ts-pattern when this is migrated to TS
    switch (assetType) {
      case graph.enums.AssetType.BankOwned:
        return pageClassificationConstants.REOPDPView
      case graph.enums.AssetType.Foreclosure:
        return pageClassificationConstants.TPSPDPView
      case graph.enums.AssetType.PrivateSeller:
        return pageClassificationConstants.PrivateSellerPDPView
      default:
        return 'PDP View'
    }
  }

  if (
    pathname.match(/\/calendar|\/event|auction-calendar/) &&
    !pathname.includes('registration')
  ) {
    return PageClassification.CalendarAndEventsView
  }

  if (
    pathname.match(/^\/registration\/event|registration/) &&
    !pathname.includes('login') &&
    !pathname.includes('blog')
  ) {
    return PageClassification.Registration
  }

  if (pathname.match(/\/dashboard|\/settings/)) {
    return PageClassification.Dashboard
  }

  // TODO: what are these pages ?
  if (pathname.includes('deals')) {
    return PageClassification.PromotionsPage
  }
  if (pathname.includes('tracker')) {
    return PageClassification.PropertyTracker
  }

  if (pathname.includes('agent')) {
    return PageClassification.AgentPages
  }

  // Wordpress Pages
  if (
    pathname.match(
      /lp\/find-local-properties|\/lp\/properties-near-you|\/lp\/roi/
    )
  ) {
    return PageClassification.MediaLPView
  }

  if (pathname.match(/^\/lp\/foreclosure\//)) {
    return PageClassification.ALCStatePage
  }

  if (pathname.includes('/blog')) {
    return PageClassification.Blog
  }

  if (pathname.includes('/about')) {
    return PageClassification.AboutUs
  }

  if (pathname.includes('/contact') && !pathname.includes('/details')) {
    return PageClassification.ContactUs
  }

  if (pathname.includes('/legal/')) {
    return PageClassification.Legal
  }

  if (pathname.includes('/learn-more')) {
    return PageClassification.HelpCenter
  }

  if (pathname.match(/\/careers|\/job|jobs|\/apply/)) {
    return PageClassification.CareersPages
  }

  return null
}

const virtualPages = new Set<string>([
  pageClassificationConstants.SRPView,
  pageClassificationConstants.Registration,
])

// PDP page classifications where the AI Assistant can be surfaced.
// getPageClassification returns 'PDP View' as the default for /details when
// the asset type is unknown, so include it alongside the typed PDP views.
const pdpPages = new Set<string>([
  pageClassificationConstants.REOPDPView,
  pageClassificationConstants.TPSPDPView,
  pageClassificationConstants.PrivateSellerPDPView,
  'PDP View',
])

class PageAnalyticsContext {
  private initialData: AnalyticsData
  private featureFlags: Record<string, any>
  private context: PageContext

  constructor(initialData: AnalyticsData = {}) {
    const pageClassification = getPageClassification(initialData?.listing)

    this.initialData = initialData
    this.featureFlags = initialData.featureFlags || {}

    this.context = {
      pageClassification,
    }

    if (initialData.user && initialData.user.logged_in) {
      this.context.userId = initialData.user.user_id || null
      this.context.partyId = initialData.user.party_id || null
    }

    if (initialData.listing) {
      this.context.morpheusPropertyId = initialData.listing.listing_id || null
      this.context.morpheusListingId =
        initialData.listing.primary_property?.property_id || null
      this.context.statusLabel =
        this.initialData.listing.listing_status_label || null

      this.context = {
        ...this.context,
        ...standardTracking.listing(this.initialData.listing),
      }
    }

    this.context.isVirtual = pageClassification
      ? virtualPages.has(pageClassification)
      : false

    // AI Assistant enabled/disabled — PDP only. Mirrors the GA dataLayer
    // value (fe.resi.ai-assistant flag) so Amplitude Page Viewed events can
    // be segmented for AI Assistant click-through-rate.
    if (pageClassification && pdpPages.has(pageClassification)) {
      this.context.aiAssistant = this.featureFlags['fe.resi.ai-assistant']
        ?.enabled
        ? 'enabled'
        : 'disabled'
    }
  }

  getFeatureFlags(): Record<string, any> {
    return this.featureFlags
  }

  getUserId(): string | null | undefined {
    return this.context.userId
  }

  getContext(): PageContext {
    return this.context
  }
}

const createAnalyticsContext = (
  initialData: AnalyticsData = {}
): PageAnalyticsContext => new PageAnalyticsContext(initialData)

export {
  getPageClassification,
  pageClassificationConstants,
  createAnalyticsContext,
  amplitudeEventConfig,
  getAmplitudeEventConfig,
  keysToLowerCamelCase,
  PageAnalyticsContext,
}

export type { AnalyticsData }
